import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import s from "../modules/Map.module.css";
import { useT, useI18nReady } from "../components/TextProvider";
import {
  getCurrentUser,
  getStudentProgress,
  initializeProgress,
  getLessonStatus,
} from "../utils/progressService";

/**
 * lessons item supports:
 *  - rating?: number (0..3)  // stars above marker
 */
export default function MapPage({


  
  subjectTitle = "Yo'l xaritasi",
  lessons: propLessons,
  onLessonSelect,
}) {

  const t = useT();
  const ready = useI18nReady();

  const navigate = useNavigate?.();
  const { id } = useParams();
  const [lessons, setLessons] = useState(propLessons || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [progressLoading, setProgressLoading] = useState(true);
  const [subjectName, setSubjectName] = useState(subjectTitle);
  
  // Используем ref для отслеживания инициализации (не вызовет ререндер)
  const initializeAttemptedRef = useRef(false);

  // Загрузка названия предмета
  useEffect(() => {
    if (!id) return;

    const fetchSubjectName = async () => {
      try {
        const fanUrl = `https://apitr.subxonqulov.uz/api/fanlar/${id}/`;
        console.log("Fetching subject name from:", fanUrl);
        
        const response = await fetch(fanUrl);
        if (response.ok) {
          const data = await response.json();
          if (data.name) {
            console.log("Subject name loaded:", data.name);
            setSubjectName(data.name);
          }
        }
      } catch (err) {
        console.warn("Error loading subject name:", err);
        // Если ошибка, используем значение по умолчанию
      }
    };

    fetchSubjectName();
  }, [id]);

  // Загрузка и инициализация прогресса
  useEffect(() => {
    let cancelled = false;

    async function loadProgress() {
      try {
        const userId = getCurrentUser();
        
        console.log("Loading progress for id:", id, "userId:", userId);
        
        if (!userId || !id) {
          console.log("No userId or id, skipping progress load", { userId, id });
          setProgressLoading(false);
          setProgressData(null);
          return;
        }

        setProgressLoading(true);

        console.log("Calling initializeProgress with fanId:", id, "userId:", userId);
        
        // Инициализируем прогресс (создаём, если нет)
        const progress = await initializeProgress(id, userId);
        console.log("✓ Loaded progress after init:", progress);
        
        if (!cancelled) {
          setProgressData(progress);
          console.log("✓ Set progressData state to:", progress);
          setProgressLoading(false);
        }
      } catch (e) {
        console.warn("✗ Error loading progress:", e);
        if (!cancelled) {
          setProgressLoading(false);
          setProgressData(null);
        }
      }
    }

    if (id) {
      console.log("useEffect triggered: id changed to", id);
      loadProgress();
    }
    
    // Cleanup функция для отмены асинхронной операции
    return () => { cancelled = true; };
  }, [id]);

  // Слушаем событие изменения авторизации и переинициализируем прогресс
  useEffect(() => {
    const handleAuthChanged = () => {
      console.log("Auth state changed, reloading progress");
      setProgressLoading(true);
      
      const userId = getCurrentUser();
      if (userId && id) {
        (async () => {
          const progress = await initializeProgress(id, userId);
          setProgressData(progress);
          setProgressLoading(false);
        })();
      } else {
        setProgressData(null);
        setProgressLoading(false);
      }
    };

    window.addEventListener("auth:changed", handleAuthChanged);
    return () => window.removeEventListener("auth:changed", handleAuthChanged);
  }, [id]);

  // API dan ma'lumotlarni yuklash
  useEffect(() => {
    if (!id && !propLessons) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);


        // Agar lessons prop sifatida uzatilgan bo'lsa, uni ishlatamiz
        if (propLessons) {
          setLessons(propLessons);
          setLoading(false);
          return;
        }

        // Darslarni yuklash
        const lessonsUrl = `https://apitr.subxonqulov.uz/api/vazifalar/?fan=${id}`;
        
        const lessonsResponse = await fetch(lessonsUrl);
        
        if (!lessonsResponse.ok) throw new Error("Failed to fetch lessons");
        const lessonsData = await lessonsResponse.json();

        // Nazorat ishlarini yuklash
        const testsUrl = `https://apitr.subxonqulov.uz/api/kurs-yozma-savollar/?kurs=${id}`;
     
        
        const testsResponse = await fetch(testsUrl);
        
        if (!testsResponse.ok) throw new Error("Failed to fetch tests");
        const testsData = await testsResponse.json();

        // Darslar va nazorat ishlarini birlashtirish va tartib bo'yicha saralash
        const combinedItems = [];

        // Saralash uchun indekslar bilan massivlar yaratish
        const lessonsByTartib = lessonsData.reduce((acc, lesson) => {
          const tartib = lesson.tartib ?? 0;
          if (!acc[tartib]) acc[tartib] = [];
          acc[tartib].push({
            ...lesson,
            type: "lesson",
            status: "available",
            rating: 0,
            description: lesson.name || "Dars",
            reward: "XP + tangalar",
            path: `/train/${lesson.id}`,
            uniqueId: `lesson-${lesson.id}`,
          });
          return acc;
        }, {});

        const testsByTartib = testsData.reduce((acc, test) => {
          const tartib = test.tartib ?? 0;
          if (!acc[tartib]) acc[tartib] = [];
          acc[tartib].push({
            ...test,
            type: "test",
            title: `Nazorat ishi ${test.tartib}`,
            status: "available",
            rating: 0,
            description: "Nazorat ishi",
            reward: "XP + tangalar",
            path: `/test/${id}/${test.id}`,
            uniqueId: `test-${test.id}`,
          });
          return acc;
        }, {});

        // Barcha noyob tartib qiymatlarini olamiz va saralab chiqamiz
        const allTartibs = new Set([
          ...Object.keys(lessonsByTartib).map(Number),
          ...Object.keys(testsByTartib).map(Number),
        ]);
        const sortedTartibs = Array.from(allTartibs).sort((a, b) => a - b);

        // Darslar va testlarni tartib tartibida birlashtiramiz
        for (const tartib of sortedTartibs) {
          if (lessonsByTartib[tartib]) {
            combinedItems.push(...lessonsByTartib[tartib]);
          }
          if (testsByTartib[tartib]) {
            combinedItems.push(...testsByTartib[tartib]);
          }
        }


        // Переумеровываем все элементы последовательно
        const renumberedItems = combinedItems.map((item, index) => ({
          ...item,
          sequenceNumber: index + 1,
        }));

        // Agar API dan ma'lumot bo'lmasa, demo ma'lumotlarni ishlatamiz
        if (renumberedItems.length === 0) {
          const demoItems = Array.from({ length: 16 }).map((_, i) => ({
            id: i + 1,
            tartib: i + 1,
            title: `Dars ${i + 1}`,
            xp: 50 + i * 10,
            durationMin: 8 + (i % 4) * 2,
            status: i < 6 ? "completed" : i === 6 ? "available" : "locked",
            rating: i < 6 ? ((i % 3) + 1) : 0,
            description:
              "Darsning qisqa tavsifi. Bu yerda maqsadlar, misollar va keyingi narsalarni ko'rsatish mumkin.",
            reward: i === 6 ? "Fokus shoshilishi +10 min" : "XP + tangalar",
            path: `/lesson/${i + 1}`,
            type: "lesson",
            sequenceNumber: i + 1,
          }));
          setLessons(demoItems);
        } else {
          setLessons(renumberedItems);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
        // Xatolik bo'lsa demo ma'lumotlarni ishlatamiz
        const demoItems = Array.from({ length: 16 }).map((_, i) => ({
          id: i + 1,
          tartib: i + 1,
          title: `Dars ${i + 1}`,
          xp: 50 + i * 10,
          durationMin: 8 + (i % 4) * 2,
          status: i < 6 ? "completed" : i === 6 ? "available" : "locked",
          rating: i < 6 ? ((i % 3) + 1) : 0,
          description:
            "Darsning qisqa tavsifi. Bu yerda maqsadlar, misollar va keyingi narsalarni ko'rsatish mumkin.",
          reward: i === 6 ? "Fokus shoshilishi +10 min" : "XP + tangalar",
          path: `/lesson/${i + 1}`,
          type: "lesson",
        }));
        setLessons(demoItems);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, propLessons]);

  // Применяем статус прогресса к урокам
  const lessonsWithStatus = useMemo(() => {
    console.log("useMemo: Processing lessons", { 
      lessonsLength: lessons.length, 
      progressLoading, 
      progressData: !!progressData,
      lessonLoading: loading
    });

    if (!lessons.length) {
      return lessons;
    }

    // Если уроки или прогресс еще загружаются - блокируем все
    if (loading || progressLoading) {
      return lessons.map(lesson => ({
        ...lesson,
        status: "locked",
      }));
    }

    // Если прогресс не получен - блокируем все
    if (!progressData) {
      return lessons.map(lesson => ({
        ...lesson,
        status: "locked",
      }));
    }

    
    // Теперь передаём массив всех уроков в getLessonStatus
    const result = lessons.map((lesson, idx) => {
      const status = getLessonStatus(lesson, progressData, lessons);
      if (idx < 3) {
        console.log(`Lesson ${idx}: id=${lesson.id}, type=${lesson.type}, status=${status}`);
      }
      return {
        ...lesson,
        status,
      };
    });
    
    console.log("Result after status assignment:", result.slice(0, 2));
    return result;
  }, [lessons, progressData, progressLoading, loading]);

  const list = useMemo(() => {
    return lessonsWithStatus && lessonsWithStatus.length > 0
      ? lessonsWithStatus
      : Array.from({ length: 16 }).map((_, i) => ({
          id: i + 1,
          title: `Dars ${i + 1}`,
          xp: 50 + i * 10,
          durationMin: 8 + (i % 4) * 2,
          status: i < 6 ? "completed" : i === 6 ? "available" : "locked",
          rating: i < 6 ? ((i % 3) + 1) : 0,
          description:
            "Darsning qisqa tavsifi. Bu yerda maqsadlar, misollar va keyingi narsalarni ko'rsatish mumkin.",
          reward: i === 6 ? "Fokus shoshilishi +10 min" : "XP + tangalar",
          path: `/lesson/${i + 1}`,
        }));
  }, [lessonsWithStatus]);

  // Darslarni har birida 4 ta dars bilan slaydlarga bo'lish
  const LESSONS_PER_SLIDE = 4;
  const slides = useMemo(() => {
    const result = [];
    for (let i = 0; i < list.length; i += LESSONS_PER_SLIDE) {
      result.push(list.slice(i, i + LESSONS_PER_SLIDE));
    }
    return result;
  }, [list]);

  const backgroundImages = [
    "/images/1.gif",
    "/images/88.gif",
    "/images/99.gif",
    "/images/1010.gif",
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [selected, setSelected] = useState(null);
  const autoSlideSetRef = useRef(false);

  // Автоматически переходим на слайд с первым доступным уроком
  useEffect(() => {
    if (autoSlideSetRef.current) return;
    if (loading || progressLoading) return;
    if (!lessonsWithStatus.length) return;

    const firstAvailableIdx = lessonsWithStatus.findIndex(l => l.status === "available");

    let targetIdx = 0;
    if (firstAvailableIdx !== -1) {
      targetIdx = firstAvailableIdx;
    } else {
      // Все уроки пройдены — показываем последний слайд
      let lastCompleted = -1;
      for (let i = lessonsWithStatus.length - 1; i >= 0; i--) {
        if (lessonsWithStatus[i].status === "completed") { lastCompleted = i; break; }
      }
      if (lastCompleted !== -1) targetIdx = lastCompleted;
    }

    setCurrentSlide(Math.floor(targetIdx / LESSONS_PER_SLIDE));
    autoSlideSetRef.current = true;
  }, [lessonsWithStatus, loading, progressLoading]);

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev < slides.length - 1 ? prev + 1 : prev));
  };

  const currentLessons = slides[currentSlide] || [];
  const currentBg = backgroundImages[currentSlide % backgroundImages.length];

  // Проверяем статус всех уроков на текущем слайде
  const allLessonsCompleted = currentLessons.length > 0 && 
    currentLessons.every(lesson => (lesson.status || "locked") === "completed");

  const openLesson = (lesson) => setSelected(lesson);

  const startLesson = (lesson) => {
    if (!lesson) return;
    if ((lesson.status || "locked") === "locked") return;

    setSelected(null);

    if (lesson.path && typeof navigate === "function") {
      navigate(lesson.path);
      return;
    }
    if (onLessonSelect) onLessonSelect(lesson);
  };

  return (
    <div className={s.mapPageSlider}>
      <header className={s.sliderHeader}>
        <div>
          <div className={s.kicker2}>{t('Yolxarita')}</div>
          <h1 className={s.title2}>{subjectName}</h1>
          <div className={s.sub2}>
            {t('Darslar')} <b>{list.length}</b> • {t('Slayd')} <b>{currentSlide + 1}/{slides.length}</b>
          </div>
        </div>

        {/* <div className={s.headerRight2}>
          <div className={s.pill2}>🔥 Seriya: <b>3</b></div>
          <div className={s.pill2}>✨ XP: <b>1240</b></div>
          <div className={s.avatar2}>
            <span className={s.avatarFallback}>AI</span>
          </div>
        </div> */}
      </header>

      <section className={s.sliderContainer}>
        {/* Oldingi slayd (o'ngda, qisman ko'rinadi) */}
        {slides.length > 1 && (
          <SlideCard
            lessons={slides[(currentSlide - 1 + slides.length) % slides.length]}
            backgroundImage={backgroundImages[((currentSlide - 1 + slides.length) % slides.length) % backgroundImages.length]}
            position="prev"
            onLessonSelect={openLesson}
          />
        )}

        {/* Joriy slayd */}
        <SlideCard
          lessons={currentLessons}
          backgroundImage={currentBg}
          position="current"
          onLessonSelect={openLesson}
        />

        {/* Keyingi slayd (chapda, qisman ko'rinadi) */}
        {slides.length > 1 && (
          <SlideCard
            lessons={slides[(currentSlide + 1) % slides.length]}
            backgroundImage={backgroundImages[((currentSlide + 1) % slides.length) % backgroundImages.length]}
            position="next"
            onLessonSelect={openLesson}
          />
        )}

        {/* Navigatsiya tugmalari */}
        <button
          className={s.navButton}
          onClick={goToPrevious}
          aria-label="Oldingi slayd"
        >
          <img src="/images/right-arrow.png" alt="next" />
        </button>
        <button
          className={s.navButton}
          onClick={goToNext}
          aria-label="Keyingi slayd"
        >
          <img src="/images/right-arrow.png" alt="next" />
        </button>

        {/* Slayd indikatorlari */}
        {/* <div className={s.slideIndicators}>
          {slides.map((_, idx) => (
            <button
              key={idx}
              className={[s.indicator, idx === currentSlide ? s.active : ""].join(" ")}
              onClick={() => setCurrentSlide(idx)}
              aria-label={`Slayd ${idx + 1}`}
            />
          ))}
        </div> */}
      </section>

      <LessonModal
        open={!!selected}
        lesson={selected}
        onClose={() => setSelected(null)}
        onStart={() => startLesson(selected)}
      />
    </div>
  );
}

// Slayd uchun komponent
function SlideCard({ lessons, backgroundImage, position, onLessonSelect }) {
  const slideClassName = [s.slide, s[`slide${position === 'prev' ? 'Prev' : position === 'next' ? 'Next' : 'Current'}`]].join(" ");
  
  // Tekshiryapmiz, bu slayddagi barcha darslar bajarilganmi
  const allLessonsCompleted = lessons.length > 0 && 
    lessons.every(lesson => (lesson.status || "locked") === "completed");
  
  return (
    <div
      className={slideClassName}
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Qorong'ining o'qilish uchun */}
      <div className={s.slideOverlay} />

      {/* Slaydning barcha darslarini bajarish indikatori */}
      {position === 'current' && allLessonsCompleted && (
        <div className={s.slideCompletedBadge}>
          <span className={s.slideCompletedIcon}>✓</span>
          <span className={s.slideCompletedText}>BARCHASI BAJARILGAN</span>
        </div>
      )}

      {/* Yo'lak bilan SVG */}
      <svg
        className={s.pathSvg}
        viewBox="0 0 1000 600"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          {/* Nuqtalar uchun gradient */}
          <radialGradient id="dotGradient" cx="30%" cy="30%">
            <stop offset="0%" stopColor="rgba(255, 200, 100, 1)" />
            <stop offset="100%" stopColor="rgba(200, 120, 30, 0.8)" />
          </radialGradient>
        </defs>

        {/* Darslar orasidagi egri chiziqlar */}
        {lessons.length > 1 && renderConnectionCurves(lessons)}
      </svg>

      {/* Darslar bilan konteyner */}
      <div className={s.lessonsContainer}>
        {lessons.map((lesson, i) => {
          const ratio = lessons.length === 1 ? 0.5 : i / (lessons.length - 1);
          const position = getPathPosition(ratio);
          const status = lesson.status || "locked";
          const rating = clampInt(lesson.rating ?? 0, 0, 3);

          return (
            <div
              key={lesson.uniqueId || lesson.id}
              className={s.lessonMarkerWrap}
              style={{
                left: `${position.xPct}%`,
                top: `${position.yPct}%`,
              }}
            >
              {/* Marker ustidagi yulduzlar */}
              {/* <div className={s.starsOverMarker} aria-label={`Baho ${rating} dan 3`}>
                <Star filled={rating >= 1} />
                <Star filled={rating >= 2} />
                <Star filled={rating >= 3} />
              </div> */}

              {/* Дars markeri */}
              <button
                type="button"
                className={[
                  s.lessonMarker,
                  status === "locked" ? s.locked : "",
                  status === "completed" ? s.done : s.avail,
                  lesson.type === "test" ? s.testMarker : "",
                ].join(" ")}
                onClick={() => onLessonSelect(lesson)}
                aria-label={`${lesson.title} ni ochish`}
              >
                <span className={s.markerHalo} />
                <span className={s.markerCore}>
                  <img src="/images/cactus.png" alt="Dars" className={s.cactusImage} />
                  <span className={s.markerNum}>
                    {lesson.type === "test" 
                      ? `${String(lesson.sequenceNumber || lesson.id).padStart(2, "0")}`
                      : String(lesson.sequenceNumber || lesson.id).padStart(2, "0")
                    }
                  </span>
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Yo'lak bo'yicha joylashuv hisoblash funktsiyasi
function getPathPosition(ratio) {
  // Piksel yo'li: M 100 300 L 250 300 L 250 150 L 400 150 L 400 350 L 550 350 L 550 200 L 700 200 L 700 400 L 850 400 L 900 300
  // Darslarni to'g'ri chiziqlar bo'ylab teng ravishda taqsimlaymiz, o'tkir burchaklar bilan
  
  const segments = [
    { x1: 100, y1: 300, x2: 250, y2: 300 },
    { x1: 250, y1: 300, x2: 250, y2: 150 },
    { x1: 250, y1: 150, x2: 400, y2: 150 },
    { x1: 400, y1: 150, x2: 400, y2: 350 },
    { x1: 400, y1: 350, x2: 550, y2: 350 },
    { x1: 550, y1: 350, x2: 550, y2: 200 },
    { x1: 550, y1: 200, x2: 700, y2: 200 },
    { x1: 700, y1: 200, x2: 700, y2: 400 },
    { x1: 700, y1: 400, x2: 850, y2: 400 },
    { x1: 850, y1: 400, x2: 900, y2: 300 },
  ];

  const totalLength = segments.reduce((sum, seg) => {
    const dx = seg.x2 - seg.x1;
    const dy = seg.y2 - seg.y1;
    return sum + Math.sqrt(dx * dx + dy * dy);
  }, 0);

  let distance = ratio * totalLength;
  for (const seg of segments) {
    const dx = seg.x2 - seg.x1;
    const dy = seg.y2 - seg.y1;
    const segmentLength = Math.sqrt(dx * dx + dy * dy);
    if (distance <= segmentLength) {
      const t = distance / segmentLength;
      const xPct = (seg.x1 + t * dx) / 10;
      const yPct = (seg.y1 + t * dy) / 6;
      return { xPct, yPct };
    }

    distance -= segmentLength;
  }

  const lastSegment = segments[segments.length - 1];
  return { xPct: lastSegment.x2 / 10, yPct: lastSegment.y2 / 6 };
}

function renderConnectionCurves(lessons) {
  const dots = [];
  let globalDotIndex = 0;

  for (let i = 0; i < lessons.length - 1; i++) {
    const start = getPathPosition(i / (lessons.length - 1));
    const end = getPathPosition((i + 1) / (lessons.length - 1));
    
    // Bezier egri chizig'i bilan egri yo'lni yaratamiz
    const startX = start.xPct * 10;
    const startY = start.yPct * 6;
    const endX = end.xPct * 10;
    const endY = end.yPct * 6;
    
    // Chiroyli egilishlar yaratish uchun kontrol nuqtalari
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;
    
    // Yoy yaratish uchun perpendikulyar siljish
    const dx = endX - startX;
    const dy = endY - startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Har bir chiziq uchun amplitudasi va egilish yo'nalishini o'zgartiramiz
    const amplitudeVariation = [0.25, 0.4, 0.35][i % 3]; // Turli amplitudalar: 25%, 40%, 35%
    const directionMultiplier = (i % 2 === 0) ? 1 : -1; // Yo'nalishlarni almashtiramiz
    
    const offsetX = (dy / distance) * (distance * amplitudeVariation) * directionMultiplier;
    const offsetY = (-dx / distance) * (distance * amplitudeVariation) * directionMultiplier;
    
    const controlX1 = startX + (midX - startX) * 0.5 + offsetX;
    const controlY1 = startY + (midY - startY) * 0.5 + offsetY;
    const controlX2 = endX - (endX - midX) * 0.5 + offsetX;
    const controlY2 = endY - (endY - midY) * 0.5 + offsetY;
    
    // Bezier egri chizig'i bo'ylab nuqtalarni hosil qilamiz
    const numDots = 20;
    for (let j = 0; j <= numDots; j++) {
      const t = numDots > 0 ? j / numDots : 0;
      
      // Kubik Bezier egri chizig'i: B(t) = (1-t)³P0 + 3(1-t)²tP1 + 3(1-t)t²P2 + t³P3
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;
      const t2 = t * t;
      const t3 = t2 * t;
      
      const x = mt3 * startX + 3 * mt2 * t * controlX1 + 3 * mt * t2 * controlX2 + t3 * endX;
      const y = mt3 * startY + 3 * mt2 * t * controlY1 + 3 * mt * t2 * controlY2 + t3 * endY;
      
      dots.push(
        <circle
          key={`dot-${globalDotIndex}`}
          cx={x}
          cy={y}
          r="4"
          className={s.wavePathDot}
          style={{
            animationDelay: `${globalDotIndex * 0.1}s`,
          }}
        />
      );
      globalDotIndex++;
    }
  }

  return dots;
}

function LessonModal({ open, lesson, onClose, onStart }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const status = lesson?.status || "locked";
  const locked = status === "locked";
  const rating = clampInt(lesson?.rating ?? 0, 0, 3);

  return createPortal(
    <div className={s.modalOverlay} role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className={s.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={s.modalTop}>
          <div>
            <div className={s.modalKicker}>Dars</div>
            <div className={s.modalTitle}>{lesson?.description || "Dars"}</div>

            {/* <div className={s.modalStars}>
              <Star filled={rating >= 1} />
              <Star filled={rating >= 2} />
              <Star filled={rating >= 3} />
            </div> */}
          </div>
          <button className={s.modalClose} onClick={onClose} aria-label="Yopish">✕</button>
        </div>

        {/* <div className={s.modalBody}>
          <div className={s.modalBadges}>
            <span className={s.badge}>{statusLabel(status)}</span>
            <span className={s.badge}>XP: {lesson?.xp ?? 0}</span>
            {typeof lesson?.durationMin === "number" && (
              <span className={s.badge}>⏱ {lesson.durationMin} min</span>
            )}
          </div>

          {lesson?.description && <div className={s.modalText}>{lesson.description}</div>}

          {lesson?.reward && (
            <div className={s.rewardBox}>
              <div className={s.rewardTitle}>Mukofot</div>
              <div className={s.rewardText}>{lesson.reward}</div>
            </div>
          )}
        </div> */}

        <div className={s.modalBottom}>
          <button className={s.btnGhost} onClick={onClose}>Orqaga</button>
          <button className={s.btnPrimary} onClick={onStart} disabled={locked}>
            {locked ? "Imkon yo'q" : "Boshlash"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Star({ filled }) {
  return (
    <span className={[s.star, filled ? s.starOn : s.starOff].join(" ")} aria-hidden="true">
      ★
    </span>
  );
}

function statusLabel(status) {
  if (status === "completed") return "Bajarildi";
  if (status === "available") return "Mavjud";
  return "Yopiq";
}

function clampInt(v, a, b) {
  const n = Number.isFinite(v) ? Math.floor(v) : 0;
  return Math.max(a, Math.min(b, n));
}
